/*
 * product-sticky-atc.js — the redesigned product template's sticky add-to-cart bar.
 *
 * <product-sticky-atc> renders server-side (initial disabled/sold-out state mirrors
 * buy-buttons) and stays `hidden` until the buy box's main ATC button scrolls out
 * of the viewport upward. Behavior:
 *   - visibility tracks #ProductSubmitButton-{section} via a rAF-throttled
 *     passive scroll listener with a hysteresis band (show once the button's
 *     bottom is 120px above the viewport, hide once it is back within 20px).
 *     NOT an IntersectionObserver: IO only fires on intersection TRANSITIONS,
 *     and a mobile anchor jump (e.g. a TOC link) teleports the button from
 *     below-the-fold to above-the-fold without ever intersecting — the bar
 *     would never show (caught by G6 on the 390px run);
 *   - the bar's button submits the MAIN product form via its form= attribute,
 *     so add-to-cart, cart notification, and error handling are product-form.js's;
 *   - price mirror: copies #price-{section} innerHTML with id attributes stripped
 *     (the main element stays the only live region) + mirrors its `hidden` class;
 *     a MutationObserver covers every update path (variant change, unavailable);
 *   - ATC mirror: MutationObserver on the main button syncs disabled + label;
 *   - SKU mirror (bar shows the SKU only when there is no part_number): copies
 *     #Sku-{section}'s value on every variant change, hiding the SKU (and its
 *     rule) when the new variant has none (#113);
 *   - PUB_SUB_EVENTS.cartError -> the error surfaces at the bar while it's shown
 *     (main form's variant only; the specific `errors` text over generic `message`);
 *   - hides its own price line whenever the google automated-discounts app block
 *     has rendered visible content (paid traffic must not see the undiscounted
 *     price next to the discounted one);
 *   - while shown on mobile it tags <html> with .product-rd-bar-visible so the
 *     theme can offset the Shopify Inbox chat bubble (CSS owns the offset);
 *   - bails inside quick-add modals; re-inits on shopify:section:load.
 */
(function () {
  'use strict';

  if (customElements.get('product-sticky-atc')) return;

  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.closest('quick-add-modal')) {
          this.remove();
          return;
        }
        this.sectionId = this.dataset.section;
        this.mainButton = document.getElementById('ProductSubmitButton-' + this.sectionId);
        this.mainPrice = document.getElementById('price-' + this.sectionId);
        this.mainSku = document.getElementById('Sku-' + this.sectionId);
        this.barButton = this.querySelector('[data-sticky-atc]');
        this.barButtonText = this.barButton ? this.barButton.querySelector('span') : null;
        this.barPrice = this.querySelector('[data-sticky-price]');
        this.barSku = this.querySelector('[data-sticky-sku]');
        this.barSkuRule = this.querySelector('[data-sticky-sku-rule]');
        this.barError = this.querySelector('[data-sticky-error]');
        if (!this.mainButton || !this.barButton) return;

        this.shown = false;
        this.initScrollTracking();
        this.initMirrors();
        this.initCartError();
        this.syncButton();
        this.syncPrice();
        this.syncSku();
      }

      disconnectedCallback() {
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
        if (this.buttonMo) this.buttonMo.disconnect();
        if (this.priceMo) this.priceMo.disconnect();
        if (this.skuMo) this.skuMo.disconnect();
        if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
        document.documentElement.classList.remove('product-rd-bar-visible');
      }

      /* Show once the main ATC's bottom edge is 120px above the viewport top;
         hide once it is back within 20px. The dead band between the two
         thresholds is the flicker hysteresis. Position is re-derived from
         getBoundingClientRect on every (throttled) scroll, so instant anchor
         jumps land in the right state. */
      initScrollTracking() {
        this.ticking = false;
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          window.requestAnimationFrame(() => {
            this.ticking = false;
            if (!this.mainButton.isConnected) return;
            var bottom = this.mainButton.getBoundingClientRect().bottom;
            if (bottom < -120) {
              this.show();
            } else if (bottom > -20) {
              this.hide();
            }
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();
      }

      show() {
        if (this.shown) return;
        this.shown = true;
        this.removeAttribute('hidden');
        document.documentElement.classList.add('product-rd-bar-visible');
        this.syncDiscountGuard();
      }

      hide() {
        if (!this.shown) return;
        this.shown = false;
        this.setAttribute('hidden', '');
        document.documentElement.classList.remove('product-rd-bar-visible');
        if (this.barError) this.barError.hidden = true;
      }

      initMirrors() {
        if ('MutationObserver' in window) {
          this.buttonMo = new MutationObserver(() => this.syncButton());
          this.buttonMo.observe(this.mainButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
            childList: true,
            characterData: true,
            subtree: true,
          });
          if (this.mainPrice) {
            this.priceMo = new MutationObserver(() => this.syncPrice());
            this.priceMo.observe(this.mainPrice, {
              attributes: true,
              attributeFilter: ['class'],
              childList: true,
              characterData: true,
              subtree: true,
            });
          }
          if (this.mainSku && this.barSku) {
            this.skuMo = new MutationObserver(() => this.syncSku());
            this.skuMo.observe(this.mainSku, {
              attributes: true,
              attributeFilter: ['class'],
              childList: true,
              characterData: true,
              subtree: true,
            });
          }
        }
      }

      syncButton() {
        var disabled = this.mainButton.hasAttribute('disabled');
        this.barButton.toggleAttribute('disabled', disabled);
        var mainText = this.mainButton.querySelector('span');
        if (mainText && this.barButtonText) {
          var label = mainText.textContent.trim();
          if (label && this.barButtonText.textContent.trim() !== label) {
            this.barButtonText.textContent = label;
          }
        }
      }

      syncPrice() {
        if (!this.barPrice || !this.mainPrice) return;
        var clone = this.mainPrice.cloneNode(true);
        clone.querySelectorAll('[id]').forEach(function (el) {
          el.removeAttribute('id');
        });
        this.barPrice.innerHTML = clone.innerHTML;
        this.barPrice.classList.toggle('hidden', this.mainPrice.classList.contains('hidden'));
        this.syncDiscountGuard();
      }

      /* product-identifier's #Sku-* is swapped by product-info.js on variant
         change; its value span holds the bare SKU, `hidden` marks "no SKU". */
      syncSku() {
        if (!this.barSku || !this.mainSku) return;
        var valueEl = this.mainSku.querySelector('.product-rd-identifier__sku-value');
        var sku = valueEl ? valueEl.textContent.trim() : '';
        var empty = !sku || this.mainSku.classList.contains('hidden');
        if (!empty && this.barSku.textContent !== sku) this.barSku.textContent = sku;
        this.barSku.classList.toggle('hidden', empty);
        if (this.barSkuRule) this.barSkuRule.classList.toggle('hidden', empty);
      }

      /* The google automated-discounts app block injects its own price UI for
         gclid traffic; when it has VISIBLE content the bar must not show the
         undiscounted price next to it. */
      syncDiscountGuard() {
        if (!this.barPrice) return;
        var gad = document.querySelector('.shopify-app-block[id*="automated_discounts"]');
        var active = !!(gad && gad.offsetHeight > 0 && gad.textContent.trim().length > 0);
        this.barPrice.classList.toggle('product-rd-sticky__price--suppressed', active);
      }

      initCartError() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (!this.shown || !this.barError || !event) return;
          /* Only the MAIN form's errors: the add-on card and quick-add forms are
             separate <product-form>s publishing on the same channel (#111). */
          var mainForm = this.mainButton.form;
          var idInput = mainForm ? mainForm.querySelector('[name=id]') : null;
          if (idInput && String(event.productVariantId) !== String(idInput.value)) return;
          /* product-form.js publishes /cart/add.js's specific `description` as
             `errors`; `message` is the generic 'Cart Error' (#111). */
          var message = typeof event.errors === 'string' && event.errors ? event.errors : event.message || '';
          if (typeof message !== 'string') message = String(message);
          if (!message) return;
          this.barError.textContent = message;
          this.barError.hidden = false;
          window.clearTimeout(this.errorTimer);
          this.errorTimer = window.setTimeout(() => {
            if (this.barError) this.barError.hidden = true;
          }, 6000);
        });
      }
    }
  );
})();
